import api from '@/lib/api';
import type { MonthlyPrice } from '@/api/types';
import type { CacheTestResult, RunnerCacheUsage } from '@/api/caches';
import type { ScalingPreset, ScalingRule } from '@/api/scaling';
import type { CloudProvider } from '@/lib/providers';

export type McpHandoffKind = 'runner' | 'cache' | 'metrics_connection' | 'scaling_policy';

/** A runner that a cache or metrics connection update affects; `recreate` is false for unknown runners. */
export type AffectedRunner = RunnerCacheUsage;

/** What the MCP tool prepared; it never contains secrets. */
export interface McpHandoffDraft {
  kind: McpHandoffKind;
  action: 'create' | 'update';
  name: string;
  /**
   * Secrets the page asks for: `runnerToken`, `githubToken`, `hetznerAuthToken`, `providerAuthToken`, `secretKey` or
   * `header:<name>`.
   */
  secrets: string[];
  /** Runners and burst templates of providers other than Hetzner only. */
  provider?: CloudProvider;
  // Runner
  productId?: string;
  cpus?: number | null;
  memory?: number | null;
  disk?: number | null;
  location?: string;
  city?: string;
  country?: string;
  /** Set for GitHub runners and burst templates only; GitLab drafts have `executor` and `gitlabHost` instead. */
  platform?: 'GITHUB';
  github?: McpHandoffGithubScope;
  executor?: string;
  concurrency?: number;
  gitlabHost?: string;
  sshKeyIds?: string[];
  labels?: string[];
  monitoringEnabled?: boolean;
  metricsConnectionName?: string | null;
  watchdogEnabled?: boolean;
  watchdogTimeoutMinutes?: number;
  cacheName?: string | null;
  monthlyPrice?: MonthlyPrice | null;
  // Cache
  cacheId?: string | null;
  serverAddress?: string;
  insecure?: boolean;
  bucketName?: string;
  bucketLocation?: string;
  pathPrefix?: string | null;
  shared?: boolean;
  pathStyle?: boolean | null;
  accessKeyId?: string;
  // Metrics connection
  connectionId?: string | null;
  endpoint?: string;
  headers?: { name: string; newValue: boolean }[];
  // Scaling policy of an external runner (`name`); the template is `productId`, `location`, `gitlabHost`, …
  policyEnabled?: boolean;
  maxBurstRunners?: number;
  stepSize?: number;
  preset?: ScalingPreset | null;
  rules?: ScalingRule[];
  scaleInWindowMinutes?: number;
  stabilizationMinutes?: number;
  // Updates
  affectedRunners?: AffectedRunner[];
  /** External runners using a changed connection; they keep the previous settings until reinstalled. */
  affectedExternalRunners?: { runnerId: string; name: string; monitoringEnabled: boolean }[];
  /** Sent back on submit; the backend refuses the update if the setting or its runners changed since. */
  stateToken?: string;
}

export interface McpHandoffGithubScope {
  owner: string;
  repository: string | null;
  runnerGroup: string | null;
}

export interface McpHandoffResult {
  outcome: 'applied' | 'unknown';
  requestId?: string;
  runnerId?: string;
  cacheId?: string;
  connectionId?: string;
  name?: string;
  recreatedRunners?: AffectedRunner[];
}

export interface McpHandoff {
  reference: string;
  kind: McpHandoffKind;
  state: 'open' | 'done';
  createdAt: string;
  expiresAt: string;
  clientName: string | null;
  organizationId: string;
  organizationName: string | null;
  draft: McpHandoffDraft | null;
  result: McpHandoffResult | null;
}

export interface McpHandoffSubmission {
  runnerToken?: string;
  githubToken?: string;
  hetznerAuthToken?: string;
  providerAuthToken?: string;
  secretKey?: string;
  headers?: { name: string; value: string }[];
  recreateRunners?: boolean;
  stateToken?: string;
}

// The link's secret goes in the request body as `draft`: the backend's Caddy logs URLs and headers, not bodies.
const BASE = 'mcp/handoffs';

export async function getHandoff(draft: string) {
  const { data } = await api.post<McpHandoff>(`${BASE}/open`, { draft });
  return data;
}

export async function submitHandoff(draft: string, submission: McpHandoffSubmission) {
  const { data } = await api.post<McpHandoff>(`${BASE}/submit`, { ...submission, draft });
  return data;
}

export async function testHandoffCache(draft: string, secretKey: string) {
  const { data } = await api.post<CacheTestResult>(`${BASE}/test-cache`, { draft, secretKey });
  return data;
}

/** The parts of an axios error with a ProblemDetail body that the handoff page reads. */
export interface HandoffProblem {
  status?: number;
  code?: string;
  detail?: string;
  runners?: AffectedRunner[];
  sshKeyIds?: string[];
}

export function handoffProblem(error: unknown): HandoffProblem | null {
  const { response } = (error ?? {}) as {
    response?: { status?: number; data?: Record<string, unknown> | string };
  };
  if (!response) return null;
  const data = response.data && typeof response.data === 'object' ? response.data : {};
  return {
    status: response.status,
    code: typeof data.code === 'string' ? data.code : undefined,
    detail: typeof data.detail === 'string' ? data.detail : undefined,
    runners: Array.isArray(data.runners) ? (data.runners as AffectedRunner[]) : undefined,
    sshKeyIds: Array.isArray(data.sshKeyIds) ? (data.sshKeyIds as string[]) : undefined,
  };
}
